/*
 * Copyright (c) 2019. World Wide Generation Ltd
 */

import { useState } from 'react';
import { TabContent, TabPane, Button } from 'reactstrap';
import { isUserManager } from '@selectors/user';
import { connect } from 'react-redux';
import UploadMediaAndDocumentModal from '../../upload-media-documents/UploadMediaAndDocumentModal';
import EditMediaAndDocument from '../../EditMediaAndDocument';
import { DashboardSection } from '@g17eco/molecules/dashboard';
import { RootState } from '@reducers/index';
import './media-slider.scss';
import { Menu } from '@g17eco/molecules/menu/Menu';
import { DocumentsModal } from '@features/document-library/documents-modal/DocumentsModal';
import { MediaTabContent } from './MediaTabContent';
import { documentProps, getSupportedMediaType } from './utils';
import { DocumentChanges, DocumentsByType, useGetDisplayDocumentsByTypeQuery, useUpdateDisplayDocumentsMutation } from '@api/documents';
import { Loader } from '@g17eco/atoms/loader';
import { DocumentMediaType, DocumentModalAction, DocumentWithAction, InitiativeDocument } from '@g17eco/types/document';
import { generateErrorToast } from '@g17eco/molecules/toasts';

interface MediaSliderProps {
  readOnly: boolean;
  isManager: boolean;
  initiativeId: string;
}

enum DocumentModalType {
  Upload = 'upload',
  Edit = 'edit',
  Display = 'display',
}

const defaultDocumentsByType: DocumentsByType = {
  [DocumentMediaType.Image]: [],
  [DocumentMediaType.Video]: [],
  [DocumentMediaType.File]: [],
  [DocumentMediaType.Assurance]: [],
};

export const MediaSlider = (props: MediaSliderProps) => {
  const { readOnly, isManager, initiativeId } = props;

  const [activeTab, setActiveTab] = useState(DocumentMediaType.Image);
  const [mediaModal, setMediaModal] = useState<DocumentModalType>();
  const [mediaDetails, setMediaDetails] = useState<InitiativeDocument | undefined>();

  const { data: documentsByType = defaultDocumentsByType, isFetching } = useGetDisplayDocumentsByTypeQuery(initiativeId);
  const [updateDisplayDocuments, { isLoading }] = useUpdateDisplayDocumentsMutation();

  if (readOnly && Object.values(documentsByType).flat().length === 0) {
    return null;
  }

  const canEdit = !!isManager && !readOnly;
  const isDisabled = isLoading || isFetching;
  const currentDocType = documentProps.find((docType) => docType.key === activeTab) ?? documentProps[0];
  const currentDocuments = [
    ...documentsByType[DocumentMediaType.Image],
    ...documentsByType[DocumentMediaType.Video],
    ...documentsByType[DocumentMediaType.File],
  ];

  const navChange = (tabKey: DocumentMediaType) => {
    setActiveTab(tabKey);
  };

  const closeModal = () => {
    setMediaModal(undefined);
  };

  const editHandler = (media: InitiativeDocument) => {
    setMediaModal(DocumentModalType.Edit);
    setMediaDetails(media);
  };

  const handleSubmitDocuments = (documentsWithAction: DocumentWithAction[]) => {
    const documentChanges = documentsWithAction.reduce(
      (acc, doc) => {
        if (doc.action === DocumentModalAction.Add) {
          if (currentDocuments.some((d) => d._id === doc.item._id)) {
            return acc;
          }
          acc.addedIds.push(doc.item._id);
        }
        if (doc.action === DocumentModalAction.Remove) {
          acc.removedIds.push(doc.item._id);
        }
        return acc;
      },
      { addedIds: [], removedIds: [] } as DocumentChanges,
    );

    return updateDisplayDocuments({ initiativeId, data: { documentChanges } })
      .unwrap()
      .catch((error) => {
        generateErrorToast(error);
      })
      .finally(() => {
        closeModal();
      });
  };

  const buttons = canEdit
    ? [
        <Button
          className='mr-1'
          key='edit-media-document'
          disabled={isDisabled}
          onClick={() => setMediaModal(DocumentModalType.Upload)}
        >
          {<i className='fal fa-upload mr-2' />}Upload
        </Button>,
        <Button
          key='edit-media-document'
          disabled={isDisabled}
          onClick={() => setMediaModal(DocumentModalType.Display)}
        >
          {<i className='fal fa-pen-line mr-2' />}Edit display
        </Button>,
      ]
    : [];

  const menuItems = documentProps.reduce(
    (items, { key, title, icon }) => {
      const length = documentsByType[key]?.length ?? 0;
      const label = `${title}s`;
      return [
        ...items,
        {
          active: activeTab === key,
          onClick: () => activeTab !== key && navChange(key),
          label: (
            <>
              {icon ? <i className={`fas ${icon} mr-2`} /> : length}
              {` ${label}`}
            </>
          ),
        },
      ];
    },
    [] as { active: boolean; onClick: () => void; label: JSX.Element }[],
  );

  return (
    <DashboardSection
      title='Media and Documents'
      className='hidden-print flickity-wrapper g-0'
      padding={2}
      buttons={buttons}
    >
      {isDisabled ? (
        <Loader />
      ) : (
        <div className='media-container'>
          <div className='tabbed-header mb-2'>
            <Menu items={menuItems} />
          </div>
          <TabContent activeTab={activeTab}>
            <TabPane key={currentDocType.key} tabId={activeTab}>
              <MediaTabContent
                displayDocuments={documentsByType[currentDocType.key]}
                addDisplayDocument={() => setMediaModal(DocumentModalType.Display)}
                currentDocType={currentDocType}
                initiativeId={initiativeId}
                editHandler={editHandler}
                canEdit={canEdit}
              />
            </TabPane>
          </TabContent>
        </div>
      )}
      {mediaModal === DocumentModalType.Upload ? <UploadMediaAndDocumentModal isOpen toggle={closeModal} /> : null}
      {mediaModal === DocumentModalType.Edit ? (
        <EditMediaAndDocument mediaDetails={mediaDetails} isOpen toggle={closeModal} />
      ) : null}
      {mediaModal === DocumentModalType.Display && canEdit ? (
        <DocumentsModal
          modalTitle='Display media'
          documentIds={currentDocuments.map((doc) => doc._id)}
          initiativeId={initiativeId}
          isOpen
          toggle={closeModal}
          handleSubmitDocuments={handleSubmitDocuments}
          mediaType={getSupportedMediaType(activeTab)}
        />
      ) : null}
    </DashboardSection>
  );
};

const mapStateToProps = (state: RootState) => ({
  isManager: isUserManager(state),
});

export default connect(mapStateToProps)(MediaSlider);
